// @ts-strict-ignore
import { BaseDynamicArticleConnectProcessor } from './base-dynamic-article-connect.processor';
import { ArticleVO } from '@article/model/vo/article.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { Vector3 } from 'three';
import { BusbarConnectionSortGroupHelper } from '@3d-scene/helper/connection/busbar-connection-sort-group.helper';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { NumberUtils } from '@shared/util/number-utils';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Injectable } from '@angular/core';
import { AcceptingConnectionSortGroupHelper } from '@3d-scene/helper/connection/accepting-connection-sort-group.helper';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ProjectModel } from '@project/model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleChangedService } from '@3d-scene/service/article-changed.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ArticleContainerService } from '@3d-scene/service/article-container.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

@Injectable({ providedIn: 'root' })
export class BusbarArticleConnectProcessor extends BaseDynamicArticleConnectProcessor {
  private static readonly MIN_ACCEPTING_CONNECTIONS: number = 2;

  constructor(
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    localeResourceManager: LocaleResourceManager,
    articleContainerLoader: ArticleContainerLoader,
    compositeArticleCacheManager: CompositeArticleCacheManager,
    articleContainerFactory: ArticleContainerFactory,
    articleManager: ArticleManager,
    compositeArticleUtils: CompositeArticleUtils,
    compositeArticleInfoUtils: CompositeArticleInfoUtils,
    articleContainerUtils: ArticleContainerUtils,
    articleChangedService: ArticleChangedService,
    detachArticleUsageService: DetachArticleUsageService,
    private busbarConnectionSortGroupHelper: BusbarConnectionSortGroupHelper,
    protected acceptingConnectionSortGroupHelper: AcceptingConnectionSortGroupHelper,
    protected containerService: ContainerService,
    articleContainerService: ArticleContainerService,
  ) {
    super(
      projectModel,
      projectModelService,
      projectArticleService,
      projectMetaElementService,
      articleContainerLoader,
      compositeArticleCacheManager,
      articleContainerFactory,
      articleManager,
      compositeArticleUtils,
      compositeArticleInfoUtils,
      articleContainerUtils,
      localeResourceManager,
      acceptingConnectionSortGroupHelper,
      containerService,
      articleChangedService,
      detachArticleUsageService,
      articleContainerService,
    );
  }

  public preProcessWithoutRefresh(
    article: ArticleVO,
    connectionCubes: Connection[],
    searchVectorId: number = NaN,
    givingConnectionNames: string[] = null
  ): ArticleContainersWrapper[] {
    const articleWrappers: ArticleContainersWrapper[] = super.preProcessWithoutRefresh(
      article,
      connectionCubes,
      searchVectorId,
      givingConnectionNames
    );
    if (!articleWrappers || articleWrappers.length === 0) {
      throw new Error(this.localeResourceManager.getValueByKey('connectProcessor.message.error_connect_busbar'));
    }
    return articleWrappers;
  }

  protected shouldSwitchPoints(rectangle: Rectangle3D, dynamicArticleContainer: ArticleContainer): boolean {
    const direction: Vector3 = rectangle.point1.clone().sub(rectangle.point4);
    return direction.x < 0;
  }

  protected isConnectionGroupValid(connectionGroup: Connection[], acceptingRectangle: Rectangle3D, article: ArticleVO): boolean {
    if (connectionGroup.length < BusbarArticleConnectProcessor.MIN_ACCEPTING_CONNECTIONS) {
      return false;
    }
    const articleLength: number = this.getStretchLengthByAcceptingRectangle(acceptingRectangle);
    let maxArticleLength: number = ArticleUtils.getArticleBehaviorPropertyNumberValue(article, ArticleBehaviorPropertyDef.MAX_LENGTH);
    if (NumberUtils.isNotNumber(maxArticleLength) || maxArticleLength === 0) {
      return true;
    }
    maxArticleLength = UnitConverter.fromTo('mm', '3d', maxArticleLength);
    return MathUtils.compare(articleLength, maxArticleLength) < 1;
  }

  protected get ignoreDistanceBetweenAcceptingConnections(): boolean {
    return true;
  }

  protected createAcceptingConnectionGroups(sortedConnections: Connection[]): Connection[][] {
    return this.busbarConnectionSortGroupHelper.splitConnectionCubesOnGroups(
      sortedConnections,
      false,
      this.ignoreDistanceBetweenAcceptingConnections
    );
  }
}
